import React from "react"

import type { JsonFieldValue, JsonObject } from "@/types/json"

/**
 * A labeled field in a detail panel: a small caps label above its value. Shared
 * by every panel that reads out a record's fields, so a test case, a run's
 * metadata, and anything added later line up as one list.
 */
export function DetailRow({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-1">
      <dt className="text-xs font-semibold uppercase tracking-normal text-coval-text-muted">
        {label}
      </dt>
      <dd className="text-sm text-coval-text">{children}</dd>
    </div>
  )
}

/** Whether a JSON-shaped field carries anything worth rendering a row for. */
export function isNonEmptyJson(
  value: JsonFieldValue | JsonObject | null | undefined,
): boolean {
  if (value == null) {
    return false
  }
  if (Array.isArray(value)) {
    return value.length > 0
  }
  if (typeof value === "object") {
    return Object.keys(value).length > 0
  }
  if (typeof value === "string") {
    return value.trim().length > 0
  }
  return true
}

/** A value's display text, or null when the value carries nothing to show. */
export function formatValue(value: unknown): string | null {
  if (value == null) {
    return null
  }
  const text = typeof value === "string" ? value : JSON.stringify(value)
  return text.trim().length > 0 ? text : null
}

export function KeyValueChips({ entries }: { entries: [string, unknown][] }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {entries.map(([key, value]) => {
        const text = formatValue(value)
        return (
          <span
            key={key}
            className="inline-flex items-center rounded-md border border-coval-border text-xs"
          >
            <span className="bg-surface-1 px-2 py-0.5 font-medium text-coval-text-muted">
              {key}
            </span>
            {/* A key with no value still says something — that the field is
                set but empty — so the chip keeps the key and marks it. */}
            <span className="px-2 py-0.5 text-coval-text">{text ?? "—"}</span>
          </span>
        )
      })}
    </div>
  )
}

/** Entries ordered by key, case-insensitively and with numbers in order. */
export function sortEntriesByKey<T>(entries: [string, T][]): [string, T][] {
  return [...entries].sort(([a], [b]) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }),
  )
}

/**
 * Key-value pairs as a two-column list, one field per row. Reads better than
 * chips once a record carries more than a handful of fields, because every key
 * starts on the same edge and every value on the other.
 */
export function KeyValueList({ entries }: { entries: [string, unknown][] }) {
  return (
    <div className="divide-y divide-coval-border rounded-md border border-coval-border bg-surface-default font-mono text-xs">
      {entries.map(([key, value]) => (
        <div
          key={key}
          className="grid grid-cols-5 gap-3 px-2.5 py-1.5"
          data-testid="key-value-row"
        >
          <span className="col-span-2 wrap-anywhere text-coval-text-muted">
            {key}
          </span>
          <span className="col-span-3 wrap-anywhere text-coval-text">
            {formatValue(value) ?? "—"}
          </span>
        </div>
      ))}
    </div>
  )
}

export function JsonBlock({ value }: { value: unknown }) {
  return (
    <pre className="font-mono text-xs text-coval-text whitespace-pre-wrap break-words max-w-full">
      {typeof value === "string" ? value : JSON.stringify(value, null, 2)}
    </pre>
  )
}
